import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { format, startOfWeek, addDays, addWeeks, subWeeks, parseISO, isToday, isFuture } from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import { 
  Users, Clock, Calendar, FileText, ChevronLeft, ChevronRight, 
  UserCircle, Timer, Briefcase, Activity, TrendingUp, Eye, Download,
  Mail, Phone, Shield, Edit2, ShieldCheck
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import AdminTimeEntryEditDialog from '../components/AdminTimeEntryEditDialog';
import { TimeEntryActions } from '../components/TimeEntryActions';
import { TimeEntryDeleteDialog } from '../components/TimeEntryDeleteDialog';
import { AdminActiveTimers } from '../components/AdminActiveTimers';
import { useTimer } from '../contexts/TimerContext';
import UserAvatar from '../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPage = () => {
  const { user, getAuthHeader } = useAuth();
  const { t, i18n } = useTranslation();
  const { refreshActiveEntry } = useTimer();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [editingEntry, setEditingEntry] = useState(null);
  const [deletingEntry, setDeletingEntry] = useState(null);

  const getLocale = () => i18n.language === 'de' ? de : enUS;

  const fetchData = useCallback(async () => {
    setLoading(true);
    const startDate = format(currentWeek, 'yyyy-MM-dd');
    const endDate = format(addDays(currentWeek, 6), 'yyyy-MM-dd');

    const requests = [
      { name: 'summary', promise: axios.get(`${API_URL}/admin/dashboard/summary`, { headers: getAuthHeader() }), setter: setSummary, fallback: null },
      { name: 'attendance', promise: axios.get(`${API_URL}/admin/dashboard/attendance?start_date=${startDate}&end_date=${endDate}`, { headers: getAuthHeader() }), setter: setAttendance, fallback: [] },
      { name: 'time-entries', promise: axios.get(`${API_URL}/admin/dashboard/time-entries?start_date=${startDate}&end_date=${endDate}&include_active=true`, { headers: getAuthHeader() }), setter: setTimeEntries, fallback: [] },
      { name: 'employees', promise: axios.get(`${API_URL}/admin/employees`, { headers: getAuthHeader() }), setter: setEmployees, fallback: [] },
    ];

    // Each request is independent: a failure in one must NOT blank the other tabs.
    const results = await Promise.allSettled(requests.map(r => r.promise));
    results.forEach((result, i) => {
      const req = requests[i];
      if (result.status === 'fulfilled') {
        req.setter(result.value.data);
      } else {
        console.error(`Admin dashboard: failed to load ${req.name}`, result.reason);
        req.setter(req.fallback);
      }
    });
    setLoading(false);
  }, [currentWeek, getAuthHeader]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchData();
    }
  }, [user?.role, fetchData]);

  const refreshTimeData = () => {
    refreshActiveEntry();
    fetchData();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0h 0m';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Build a quick lookup so avatars show the correct user color/picture
  const userById = React.useMemo(() => {
    const m = {};
    employees.forEach(e => { m[e.id] = e; });
    return m;
  }, [employees]);

  const enrich = (id, fallbackName) => userById[id] || { id, name: fallbackName };

  const adminCount = React.useMemo(() => employees.filter(e => e.role === 'admin').length, [employees]);

  const handleRoleToggle = async (emp, makeAdmin) => {
    const newRole = makeAdmin ? 'admin' : 'user';
    const confirmMsg = makeAdmin
      ? t('admin.confirmMakeAdmin', { name: emp.name })
      : t('admin.confirmRemoveAdmin', { name: emp.name });
    if (!window.confirm(confirmMsg)) return;
    try {
      await axios.put(
        `${API_URL}/admin/employees/${emp.id}/role?role=${newRole}`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success(t('admin.roleUpdated'));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin.roleUpdateError'));
    }
  };

  const handleExport = async (type) => {
    const startDate = format(currentWeek, 'yyyy-MM-dd');
    const endDate = format(addDays(currentWeek, 6), 'yyyy-MM-dd');
    const url = `${API_URL}/admin/export/${type}?start_date=${startDate}&end_date=${endDate}`;
    try {
      const response = await axios.get(url, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `${type}_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Admin CSV export failed:', error);
    }
  };

  const handlePrevWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const handleNextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const handleCurrentWeek = () => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i));

  // Group attendance by date
  const attendanceByDate = attendance.reduce((acc, item) => {
    if (!acc[item.date]) acc[item.date] = [];
    acc[item.date].push(item);
    return acc;
  }, {});

  // Group time entries by user
  const timeEntriesByUser = timeEntries.filter(entry => entry.end_time).reduce((acc, entry) => {
    if (!acc[entry.user_id]) {
      acc[entry.user_id] = { name: entry.user_name, entries: [], totalDuration: 0 };
    }
    acc[entry.user_id].entries.push(entry);
    acc[entry.user_id].totalDuration += entry.duration || 0;
    return acc;
  }, {});

  // Group time entries by date
  const timeEntriesByDate = timeEntries.filter(entry => entry.end_time).reduce((acc, entry) => {
    if (!acc[entry.date]) acc[entry.date] = [];
    acc[entry.date].push(entry);
    return acc;
  }, {});

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-20">
          <h1 className="text-2xl font-bold text-zinc-900">{t('admin.accessDenied')}</h1>
          <p className="text-zinc-600 mt-2">{t('admin.adminOnly')}</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="admin-dashboard" className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t('admin.dashboardTitle')}
          </h1>
          <p className="text-zinc-600 mt-1">{t('admin.dashboardSubtitle')}</p>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white p-5 rounded-2xl border border-zinc-200"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#DBEAFE] flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#3B82F6]" />
                </div>
              </div>
              <div className="text-3xl font-bold text-zinc-900">{summary.total_users}</div>
              <div className="text-sm text-zinc-500">{t('admin.totalEmployees')}</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white p-5 rounded-2xl border border-zinc-200"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#D1FAE5] flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-[#10B981]" />
                </div>
              </div>
              <div className="text-3xl font-bold text-zinc-900">{summary.todays_attendance}</div>
              <div className="text-sm text-zinc-500">{t('admin.todayAttendance')}</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white p-5 rounded-2xl border border-zinc-200"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] flex items-center justify-center">
                  <Timer className="w-5 h-5 text-[#D93025]" />
                </div>
              </div>
              <div className="text-3xl font-bold text-zinc-900">{summary.active_timers}</div>
              <div className="text-sm text-zinc-500">{t('admin.activeTimers')}</div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white p-5 rounded-2xl border border-zinc-200"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-[#FEF3C7] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#F59E0B]" />
                </div>
              </div>
              <div className="text-3xl font-bold text-zinc-900">{summary.todays_entries}</div>
              <div className="text-sm text-zinc-500">{t('admin.todayEntries')}</div>
            </motion.div>
          </div>
        )}

        {/* Week Navigation */}
        <div className="flex items-center justify-between bg-white p-3 rounded-2xl border border-zinc-200 mb-6">
          <Button variant="ghost" size="sm" onClick={handlePrevWeek} className="rounded-full">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          
          <div className="text-center flex-1">
            <div className="font-semibold text-zinc-900">
              {format(currentWeek, 'd. MMM', { locale: getLocale() })} - {format(addDays(currentWeek, 6), 'd. MMM yyyy', { locale: getLocale() })}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleCurrentWeek} className="rounded-full text-xs">
              {t('admin.thisWeek')}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleNextWeek} className="rounded-full">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="attendance" className="space-y-6">
          <TabsList className="bg-zinc-100 p-1 rounded-xl flex-wrap h-auto">
            <TabsTrigger value="attendance" className="rounded-lg data-[state=active]:bg-white">
              <Calendar className="w-4 h-4 mr-2" />
              {t('admin.attendance')}
            </TabsTrigger>
            <TabsTrigger value="timeTracking" className="rounded-lg data-[state=active]:bg-white">
              <Clock className="w-4 h-4 mr-2" />
              {t('admin.timeTracking')}
            </TabsTrigger>
            <TabsTrigger value="activities" className="rounded-lg data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              {t('admin.activities')}
            </TabsTrigger>
            <TabsTrigger value="members" className="rounded-lg data-[state=active]:bg-white" data-testid="admin-members-tab">
              <Users className="w-4 h-4 mr-2" />
              {t('admin.members')}
            </TabsTrigger>
          </TabsList>

          {/* Attendance Tab */}
          <TabsContent value="attendance" className="space-y-4">
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                <h2 className="font-bold text-zinc-900 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#D93025]" />
                  {t('admin.plannedAttendance')}
                </h2>
                <p className="text-sm text-zinc-500 mt-1">{t('admin.attendanceDesc')}</p>
              </div>
              
              <div className="divide-y divide-zinc-100">
                {weekDays.map((day) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const dayAttendance = attendanceByDate[dateStr] || [];
                  const isPastDay = !isToday(day) && !isFuture(day);
                  
                  return (
                    <div key={dateStr} className={`p-4 ${isPastDay ? 'bg-zinc-50/50' : ''}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            isToday(day) ? 'bg-[#D93025] text-white' : 'bg-zinc-100 text-zinc-600'
                          }`}>
                            <span className="text-sm font-bold">{format(day, 'd')}</span>
                          </div>
                          <div>
                            <div className="font-medium text-zinc-900">
                              {format(day, 'EEEE', { locale: getLocale() })}
                              {isToday(day) && (
                                <span className="ml-2 text-xs bg-[#D93025] text-white px-2 py-0.5 rounded-full">
                                  {t('admin.today')}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-zinc-500">{format(day, 'd. MMMM', { locale: getLocale() })}</div>
                          </div>
                        </div>
                        <div className="text-sm text-zinc-500">
                          {dayAttendance.length} {t('admin.employees')}
                        </div>
                      </div>
                      
                      {dayAttendance.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {dayAttendance.map((att) => (
                            <div key={att.id} className="flex items-center gap-2 bg-zinc-100 rounded-full px-3 py-1.5">
                              <UserAvatar user={enrich(att.user_id, att.user_name)} size="sm" />
                              <span className="text-sm font-medium text-zinc-700">{att.user_name}</span>
                              {att.planned_time && (
                                <span className="text-xs text-zinc-500">{att.planned_time}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-zinc-400">{t('admin.noPlanned')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* Time Tracking Tab */}
          <TabsContent value="timeTracking" className="space-y-4">
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-bold text-zinc-900 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#D93025]" />
                    {t('admin.workHours')}
                  </h2>
                  <p className="text-sm text-zinc-500 mt-1">{t('admin.workHoursDesc')}</p>
                </div>
                <Button
                  data-testid="export-workhours-btn"
                  onClick={() => handleExport('workhours')}
                  className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full text-xs h-9"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {t('admin.exportWorkhours')}
                </Button>
              </div>
              
              <div className="px-4 pt-4"><AdminActiveTimers entries={timeEntries} onEdit={setEditingEntry} onDelete={setDeletingEntry} /></div>

              {/* Summary by User */}
              <div className="p-4 border-b border-zinc-200 bg-gradient-to-r from-zinc-50 to-white">
                <h3 className="font-semibold text-zinc-700 mb-3">{t('admin.weekSummary')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(timeEntriesByUser).map(([userId, data]) => (
                    <div key={userId} className="bg-white p-3 rounded-xl border border-zinc-200">
                      <div className="flex items-center gap-2 mb-2">
                        <UserAvatar user={enrich(userId, data.name)} size="md" />
                        <span className="font-medium text-zinc-900">{data.name}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-zinc-500">{data.entries.length} {t('admin.sessions')}</span>
                        <span className="font-bold text-[#D93025]">{formatDuration(data.totalDuration)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Entries by Day */}
              <div className="divide-y divide-zinc-100">
                {weekDays.map((day) => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const dayEntries = timeEntriesByDate[dateStr] || [];
                  const totalDuration = dayEntries.reduce((sum, e) => sum + (e.duration || 0), 0);
                  
                  if (dayEntries.length === 0) return null;
                  
                  return (
                    <div key={dateStr} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                            isToday(day) ? 'bg-[#D93025] text-white' : 'bg-zinc-100 text-zinc-600'
                          }`}>
                            <span className="text-sm font-bold">{format(day, 'd')}</span>
                          </div>
                          <div>
                            <div className="font-medium text-zinc-900">{format(day, 'EEEE', { locale: getLocale() })}</div>
                            <div className="text-sm text-zinc-500">{dayEntries.length} {t('admin.entries')}</div>
                          </div>
                        </div>
                        <div className="text-lg font-bold text-[#D93025]">{formatDuration(totalDuration)}</div>
                      </div>
                      
                      <div className="space-y-2 ml-13">
                        {dayEntries.map((entry) => (
                          <div key={entry.id} className="flex flex-wrap items-center gap-2 p-2 bg-zinc-50 rounded-lg group" data-testid={`admin-time-entry-${entry.id}`}>
                            <UserAvatar user={enrich(entry.user_id, entry.user_name)} size="sm" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-zinc-700 text-sm">{entry.user_name}</span>
                                {entry.end_time && (
                                  <span className="text-xs text-zinc-500">
                                    {format(new Date(entry.start_time), 'HH:mm')} - {format(new Date(entry.end_time), 'HH:mm')}
                                  </span>
                                )}
                                {entry.edited_by_admin && (
                                  <span
                                    className="text-[10px] uppercase tracking-wide font-bold bg-[#FEF3C7] text-[#B45309] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                                    title={entry.edited_by_name ? `${t('admin.editedBy')} ${entry.edited_by_name}` : t('adminEdit.editedBadge')}
                                  >
                                    <ShieldCheck className="w-2.5 h-2.5" />
                                    {t('adminEdit.editedBadge')}
                                  </span>
                                )}
                              </div>
                              {entry.description && (
                                <p className="text-xs text-zinc-500 mt-0.5 line-clamp-1">{entry.description}</p>
                              )}
                            </div>
                            <span className="font-semibold text-zinc-900 text-sm" data-testid={`admin-entry-duration-${entry.id}`}>{formatDuration(entry.duration)}</span>
                            <TimeEntryActions entry={entry} prefix="admin" onEdit={setEditingEntry} onDelete={setDeletingEntry} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {Object.keys(timeEntriesByDate).length === 0 && (
                  <div className="p-8 text-center text-zinc-500">
                    {t('admin.noTimeEntries')}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Activities Tab */}
          <TabsContent value="activities" className="space-y-4">
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
              <div className="p-4 border-b border-zinc-200 bg-zinc-50 flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-bold text-zinc-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#D93025]" />
                    {t('admin.activityLog')}
                  </h2>
                  <p className="text-sm text-zinc-500 mt-1">{t('admin.activityLogDesc')}</p>
                </div>
                <Button
                  data-testid="export-activities-btn"
                  onClick={() => handleExport('activities')}
                  className="bg-[#D93025] hover:bg-[#B91C1C] text-white rounded-full text-xs h-9"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  {t('admin.exportActivities')}
                </Button>
              </div>
              
              <div className="divide-y divide-zinc-100">
                {timeEntries.filter(e => e.description && e.end_time).length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">
                    {t('admin.noActivities')}
                  </div>
                ) : (
                  timeEntries.filter(e => e.description && e.end_time).map((entry, idx) => (
                    <motion.div
                      key={entry.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      className="p-4"
                    >
                      <div className="flex items-start gap-4">
                        <UserAvatar user={enrich(entry.user_id, entry.user_name)} size="lg" />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-zinc-900">{entry.user_name}</span>
                              {entry.edited_by_admin && (
                                <span
                                  className="text-[10px] uppercase tracking-wide font-bold bg-[#FEF3C7] text-[#B45309] px-1.5 py-0.5 rounded-full inline-flex items-center gap-1"
                                  title={entry.edited_by_name ? `${t('admin.editedBy')} ${entry.edited_by_name}` : t('adminEdit.editedBadge')}
                                >
                                  <ShieldCheck className="w-2.5 h-2.5" />
                                  {t('adminEdit.editedBadge')}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-sm text-zinc-500">
                                {format(new Date(entry.date), 'd. MMM', { locale: getLocale() })}
                              </span>
                              <TimeEntryActions entry={entry} prefix="admin-activity" onEdit={setEditingEntry} onDelete={setDeletingEntry} />
                            </div>
                          </div>
                          <div className="text-sm text-zinc-500 mb-2">
                            {format(new Date(entry.start_time), 'HH:mm')} - {format(new Date(entry.end_time), 'HH:mm')} ({formatDuration(entry.duration)})
                          </div>
                          <div className="bg-zinc-50 p-3 rounded-xl">
                            <p className="text-zinc-700 whitespace-pre-wrap">{entry.description}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="space-y-4">
            <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden" data-testid="admin-members-list">
              <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                <h2 className="font-bold text-zinc-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#D93025]" />
                  {t('admin.allMembers')}
                </h2>
                <p className="text-sm text-zinc-500 mt-1">{t('admin.allMembersDesc')}</p>
              </div>
              <div className="divide-y divide-zinc-100">
                {employees.length === 0 ? (
                  <div className="p-8 text-center text-zinc-500">{t('admin.noMembers')}</div>
                ) : (
                  employees.map((emp, idx) => (
                    <motion.div
                      key={emp.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      className="p-4 hover:bg-zinc-50/50 transition-colors"
                      data-testid={`admin-member-row-${emp.id}`}
                    >
                      <div className="flex items-start gap-4">
                        <UserAvatar user={emp} size="lg" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-semibold text-zinc-900">{emp.name}</span>
                            {emp.role === 'admin' && (
                              <span className="text-[10px] uppercase tracking-wide font-bold bg-[#FEE2E2] text-[#D93025] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                <Shield className="w-3 h-3" />
                                Admin
                              </span>
                            )}
                            {emp.team && (
                              <span className="text-[10px] uppercase tracking-wide font-bold bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded-full">
                                {emp.team}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-zinc-500 flex-wrap">
                            <span className="flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {emp.email}
                            </span>
                            {emp.mobile && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {emp.mobile}
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                            <div className="bg-zinc-50 rounded-lg px-3 py-2">
                              <div className="text-xs text-zinc-500">{t('admin.totalHours')}</div>
                              <div className="font-bold text-zinc-900">{emp.stats?.total_hours || 0}h</div>
                            </div>
                            <div className="bg-zinc-50 rounded-lg px-3 py-2">
                              <div className="text-xs text-zinc-500">{t('admin.tasksAssigned')}</div>
                              <div className="font-bold text-zinc-900">{emp.stats?.tasks_assigned || 0}</div>
                            </div>
                            <div className="bg-zinc-50 rounded-lg px-3 py-2">
                              <div className="text-xs text-zinc-500">{t('admin.tasksCompleted')}</div>
                              <div className="font-bold text-zinc-900">{emp.stats?.tasks_completed || 0}</div>
                            </div>
                            <div className="bg-zinc-50 rounded-lg px-3 py-2">
                              <div className="text-xs text-zinc-500">{t('admin.upcomingOfficeDays')}</div>
                              <div className="font-bold text-zinc-900">{emp.stats?.upcoming_office_days || 0}</div>
                            </div>
                          </div>
                          {/* Role toggle */}
                          <div className="mt-3 flex items-center gap-3 bg-zinc-50 rounded-lg px-3 py-2 w-fit" data-testid={`admin-role-control-${emp.id}`}>
                            <div className="flex items-center gap-1.5 text-sm text-zinc-700">
                              <ShieldCheck className="w-4 h-4 text-[#D93025]" />
                              {t('admin.role')}
                            </div>
                            <Switch
                              checked={emp.role === 'admin'}
                              disabled={emp.role === 'admin' && adminCount <= 1}
                              onCheckedChange={(checked) => handleRoleToggle(emp, checked)}
                              data-testid={`admin-role-switch-${emp.id}`}
                            />
                            <span className="text-xs font-medium text-zinc-600">
                              {emp.role === 'admin' ? t('admin.roleAdmin') : t('admin.roleUser')}
                            </span>
                            {emp.role === 'admin' && adminCount <= 1 && (
                              <span className="text-[10px] text-zinc-400">· {t('admin.lastAdminHint')}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Admin edit dialog */}
      <AdminTimeEntryEditDialog
        open={!!editingEntry}
        onOpenChange={(o) => { if (!o) setEditingEntry(null); }}
        entry={editingEntry}
        onSaved={refreshTimeData}
      />
      <TimeEntryDeleteDialog entry={deletingEntry} onClose={() => setDeletingEntry(null)} onDeleted={refreshTimeData} />
    </Layout>
  );
};

export default AdminPage;
